import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	Card,
	CardContent,
	Grid,
	Stack,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import { useNavigate, useParams } from 'react-router-dom';
import SetStatusDialog from './components/SetStatusDialog';
import {
	preAdmission,
	useDeletePreAdmissionMutation,
	useGetPreAdmissionDetailQuery,
} from '@/api/pre-admission/preAdmission';
import dayjs from 'dayjs';
import {
	DISPLAY_DATE_FORMAT,
	DISPLAY_DATE_TIME_FORMAT,
} from '@/utils/dateHelper';
import { showLabel } from '@/utils/helper';
import { formatAge } from '@/utils/ageHelper';
import EstimatedAgeInfoHint from '@/pages/member-management/admission-request-detail/components/EstimatedAgeInfoHint';
import {
	AGENCY_LIST,
	CONSENT_OBTAINED_LIST,
	RESULT_CAN_CONTACTED_TARGET_LIST,
	ENQUIRY_METHOD_LIST,
	ISCASE_LIST,
	LANGUAGE_LIST,
	PREFIX_LIST,
	SEX_LIST,
} from '@/common/constants/common';
import PreAdmissionChip from '@/common/components/PreAdmissionChip';
import { PermissionEnum, PreAdmissionTab } from '@/api/enum/common.enum';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SourceOutlinedIcon from '@mui/icons-material/SourceOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import { useGetUser } from '@/hooks/useGetUser';
import FileNoHistoryButton from '@/common/components/member-duplication/FileNoHistoryButton';

export default function PreAdmissionDetailPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { preAdmissionId } = useParams();
	const [deletePreAdmission] = useDeletePreAdmissionMutation();
	const showModal = useModal();
	const { userInfo } = useGetUser();

	const { data: details }: any = useGetPreAdmissionDetailQuery(
		preAdmissionId || '',
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const canEditWhenInProgress =
		userInfo?.permissionList?.includes(
			PermissionEnum.EditPreAdmissionWhenStatusInProgress
		) && details?.data.preAdmissionStatus === PreAdmissionTab.progress;

	const canEditWhenSubmittedOrNotSuitable =
		userInfo?.permissionList?.includes(
			PermissionEnum.EditPreAdmissionWhenStatusSubmittedOrNotSuitable
		) &&
		(details?.data.preAdmissionStatus === PreAdmissionTab.submitted ||
			details?.data.preAdmissionStatus === PreAdmissionTab.notSuitable);

	const canEditPreAdmission =
		canEditWhenInProgress || canEditWhenSubmittedOrNotSuitable;

	const handleDelete = async () => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={false}
				title={t('preAdmission.deleteConfirmation')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{t('preAdmission.isDeletePreAdmission')}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.confirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deletePreAdmission(details?.data.id || '');
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.preAdmissionDeleteSuccess'), {
							variant: 'warning',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});
						navigate(`/pre-admission/pre-admission-list`, { replace: true });
					}
				}}
			/>
		));
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Box display='flex' justifyContent='space-between' marginBottom={3}>
					<Typography variant='h4'>
						{t('drawerListItems.preAdmissionDetails')}
					</Typography>
					<Stack direction={'row'} alignItems={'center'}>
						{userInfo?.permissionList?.includes(
							PermissionEnum.SummaryOfForm
						) && (
							<Button
								endIcon={<SourceOutlinedIcon />}
								onClick={() =>
									navigate(
										`/pre-admission/pre-admission-list/detail/${preAdmissionId}/forms-management`
									)
								}
								sx={{ marginRight: '24px' }}
								variant='contained'
							>
								{t('button.manageForms')}
							</Button>
						)}
						{userInfo?.permissionList?.includes(
							PermissionEnum.ChangeStatusOfPreAdmission
						) &&
							details?.data.preAdmissionStatus === PreAdmissionTab.progress && (
								<SetStatusDialog
									alreadyLinked={
										!!(
											details?.data.linkedClosedUserId ||
											details?.data.linkedLegacyFileNo
										)
									}
								/>
							)}
						{userInfo?.permissionList?.includes(
							PermissionEnum.DeletePreAdmission
						) && (
							<Button
								endIcon={<DeleteOutlinedIcon />}
								onClick={() => {
									handleDelete();
								}}
								variant='contained'
							>
								{t('button.delete')}
							</Button>
						)}
					</Stack>
				</Box>

				{details?.data.preAdmissionStatus == PreAdmissionTab.submitted && (
					<Card
						sx={{
							marginTop: 3,
							padding: '10px 16px',
							backgroundColor: '#F0F7BA',
							borderRadius: '16px',
							color: '#5D5E61',
							display: 'flex',
							marginBottom: '24px',
						}}
					>
						<ErrorOutlineIcon sx={{ marginRight: '16px' }} />
						{t('preAdmission.detailsTips1')}
					</Card>
				)}
				{details?.data.preAdmissionStatus == PreAdmissionTab.notSuitable && (
					<Card
						sx={{
							marginTop: 3,
							padding: '10px 16px',
							backgroundColor: '#F0F7BA',
							borderRadius: '16px',
							color: '#5D5E61',
							display: 'flex',
							marginBottom: '24px',
						}}
					>
						<ErrorOutlineIcon sx={{ marginRight: '16px' }} />
						{t('preAdmission.detailsTips2')}
					</Card>
				)}

				{/* Pre-admission details */}
				<Accordion
					defaultExpanded
					sx={{
						borderRadius: '16px!important',
						'&::before': {
							backgroundColor: 'transparent !important',
						},
						marginBottom: '24px',
						padding: '8px',
					}}
				>
					<AccordionSummary expandIcon={<ExpandMoreIcon />}>
						<Typography
							component='span'
							sx={{ width: '99%', flexShrink: 1, fontWeight: 'bold' }}
						>
							{t('preAdmission.preAdmissionDetails')}
						</Typography>
						{canEditPreAdmission && (
							<Button
								onClick={(e) => {
									e.stopPropagation();
									navigate(
										`/pre-admission/pre-admission-list/detail/${preAdmissionId}/edit-pre-admission`
									);
								}}
								sx={{ marginRight: '24px', textTransform: 'none' }}
								variant='outlined'
							>
								{t('button.edit')}
							</Button>
						)}
					</AccordionSummary>
					<AccordionDetails>
						<Grid container spacing={3}>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.preAdmissionNumber')}
									value={details?.data.preAdmissionNo}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.fileNumber')}
									value={details?.data.fileNo}
									editButton={
										<FileNoHistoryButton
											userId={details?.data.id}
											fileNo={details?.data.fileNo}
											hasFileNoHistory={details?.data.hasFileNoHistory}
										/>
									}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.unit')}
									value={details?.data.branchName}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.enquiryDate')}
									value={
										details?.data.enquiryDate
											? dayjs(details?.data.enquiryDate)
													.locale('en')
													.format(DISPLAY_DATE_FORMAT)
											: ''
									}
								/>
							</Grid>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.enquiryMethod')}
									value={showLabel(
										details?.data.enquiryMethod,
										ENQUIRY_METHOD_LIST
									)}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.creator')}
									value={details?.data.createdBy}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.lastModified')}
									value={
										details?.data.updatedAt
											? dayjs(details?.data.updatedAt)
													.locale('en')
													.format(DISPLAY_DATE_TIME_FORMAT)
											: ''
									}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.preAdmissionStatus')}
									valueElement={
										<PreAdmissionChip
											status={details?.data.preAdmissionStatus}
											label={details?.data.preAdmissionStatusDesc}
										/>
									}
								/>
							</Grid>
						</Grid>
					</AccordionDetails>
				</Accordion>
				{/* clientInformation */}
				<Accordion
					defaultExpanded
					sx={{
						borderRadius: '16px!important',
						'&::before': {
							backgroundColor: 'transparent !important',
						},
						marginBottom: '24px',
						padding: '8px',
					}}
				>
					<AccordionSummary expandIcon={<ExpandMoreIcon />}>
						<Typography
							component='span'
							sx={{ width: '99%', flexShrink: 1, fontWeight: 'bold' }}
						>
							{t('preAdmission.clientInformation')}
						</Typography>
						{canEditPreAdmission && (
							<Button
								onClick={(e) => {
									e.stopPropagation();
									navigate(
										`/pre-admission/pre-admission-list/detail/${preAdmissionId}/edit-client-information`
									);
								}}
								sx={{ marginRight: '24px', textTransform: 'none' }}
								variant='outlined'
							>
								{t('button.edit')}
							</Button>
						)}
					</AccordionSummary>
					<AccordionDetails>
						<Grid container spacing={3}>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.name_en')}
									value={details?.data.nameEn}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.name_zh')}
									value={details?.data.nameZh}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.dateOfBirth')}
									value={
										details?.data.dateOfBirth
											? dayjs(details?.data.dateOfBirth)
													.locale('en')
													.format(DISPLAY_DATE_FORMAT)
											: ''
									}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.currentAge')}
									labelElement={
										details?.data.currentAgeEstimated && (
											<EstimatedAgeInfoHint
												currentAge={details?.data.currentAge}
											/>
										)
									}
									value={formatAge(
										details?.data.currentAge,
										details?.data.currentAgeEstimated
									)}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.sex')}
									value={showLabel(details?.data.sex, SEX_LIST)}
								/>
							</Grid>
							<Grid size={6} />
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.mobilePhone')}
									value={details?.data.phone}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.maritalStatus')}
									value={details?.data.maritalStatusDesc}
								/>
							</Grid>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.addressLanguage')}
									value={showLabel(
										details?.data.addressLanguage,
										LANGUAGE_LIST
									)}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.region')}
									value={details?.data.regionDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.district')}
									value={details?.data.districtDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.subDistrict')}
									value={details?.data.subdistrictDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.estate/court/village')}
									value={details?.data.estateCourtVillageDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.road/street')}
									value={details?.data.roadStreet}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.road/streetNo')}
									value={details?.data.roadStreetNo}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.building/block')}
									value={details?.data.buildingBlockDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.building/block(specify)')}
									value={details?.data.buildingBlockSpecify}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.floor')}
									value={details?.data.floor}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.flat')}
									value={details?.data.flat}
								/>
							</Grid>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.fullAddress')}
									value={details?.data.fullAddress}
								/>
							</Grid>
						</Grid>
					</AccordionDetails>
				</Accordion>
				{/* Referrer information */}
				<Accordion
					defaultExpanded
					sx={{
						borderRadius: '16px!important',
						'&::before': {
							backgroundColor: 'transparent !important',
						},
						marginBottom: '24px',
						padding: '8px',
					}}
				>
					<AccordionSummary expandIcon={<ExpandMoreIcon />}>
						<Typography
							component='span'
							sx={{ width: '99%', flexShrink: 1, fontWeight: 'bold' }}
						>
							{t('preAdmission.referrerInformation')}
						</Typography>
						{canEditPreAdmission && (
							<Button
								onClick={(e) => {
									e.stopPropagation();
									navigate(
										`/pre-admission/pre-admission-list/detail/${preAdmissionId}/edit-referrer-information`
									);
								}}
								sx={{ marginRight: '24px', textTransform: 'none' }}
								variant='outlined'
							>
								{t('button.edit')}
							</Button>
						)}
					</AccordionSummary>
					<AccordionDetails>
						<Grid container spacing={3}>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.approachAource')}
									value={details?.data.approachSourceDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.profileOfReferrals')}
									value={details?.data.profileOfReferralDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.profileOfReferrals(specify)')}
									value={details?.data.profileOfReferralSpecify}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.referralAgency')}
									value={details?.data.referralAgencyDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.referralAgency(specify)')}
									value={details?.data.referralAgencySpecify}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.prefix')}
									value={showLabel(details?.data.prefixOfReferrer, PREFIX_LIST)}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.nameOfReferrer')}
									value={details?.data.nameOfReferrer}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.positionOfReferrer')}
									value={details?.data.positionOfReferrer}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.referrerContactNo')}
									value={details?.data.referrerContactNo}
								/>
							</Grid>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.referrerFaxNo')}
									value={details?.data.referrerFaxNo}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.howtheCaseKnownToReferrer')}
									value={details?.data.howTheCaseKnownToReferrer}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.caseNatureOfTheAgency')}
									value={showLabel(
										details?.data.caseNatureOfTheAgency,
										AGENCY_LIST
									)}
								/>
							</Grid>
						</Grid>
					</AccordionDetails>
				</Accordion>
				{/* Case status  */}
				<Accordion
					defaultExpanded
					sx={{
						borderRadius: '16px!important',
						'&::before': {
							backgroundColor: 'transparent !important',
						},
						marginBottom: '24px',
						padding: '8px',
					}}
				>
					<AccordionSummary expandIcon={<ExpandMoreIcon />}>
						<Typography
							component='span'
							sx={{ width: '99%', flexShrink: 1, fontWeight: 'bold' }}
						>
							{t('preAdmission.caseStatus')}
						</Typography>
						{canEditPreAdmission && (
							<Button
								onClick={(e) => {
									e.stopPropagation();
									navigate(
										`/pre-admission/pre-admission-list/detail/${preAdmissionId}/edit-case-status`
									);
								}}
								sx={{ marginRight: '24px', textTransform: 'none' }}
								variant='outlined'
							>
								{t('button.edit')}
							</Button>
						)}
					</AccordionSummary>
					<AccordionDetails>
						<Grid container spacing={3}>
							<Grid size={6}>
								{/* Reads "Is a potential case", matching the create and edit
								    forms; nothing at pre-admission is a case yet. The field
								    and the API column stay isCase. */}
								<AppFieldView
									label={t('preAdmission.isPotentialCase')}
									value={showLabel(details?.data.isCase, ISCASE_LIST)}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.caseStatus')}
									valueElement={
										<>
											{details?.data.suspectedMentalHealthProblems && (
												<Typography variant='body2'>
													{t('preAdmission.suspectedMentalHealthProblems')}
												</Typography>
											)}
											{details?.data.defaultedPsychiatricFollowUp && (
												<Typography variant='body2'>
													{`${t(
														'preAdmission.defaultedPsychiatricFollowUpSince'
													)} ${
														details?.data.defaultedPsychiatricFollowUpSince
													}`}
												</Typography>
											)}
											{details?.data.awaitingPsychiatricFollowUp && (
												<Typography variant='body2'>
													{t('preAdmission.awaitingPsychiatricFollowUp')}
												</Typography>
											)}
											{details?.data.hasRegularPsychiatricFollowUp && (
												<Typography variant='body2'>
													{`${t(
														'preAdmission.hasRegularPsychiatricFollowUpSince'
													)} ${details?.data.regularPsychiatricFollowUpSince}`}
												</Typography>
											)}
										</>
									}
								/>
							</Grid>

							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.dateOfReceivingReferral/Application')}
									value={
										details?.data.receivingReferralOrApplicationDate
											? dayjs(details?.data.receivingReferralOrApplicationDate)
													.locale('en')
													.format(DISPLAY_DATE_FORMAT)
											: ''
									}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.consentObtained')}
									value={
										details?.data.consentObtained == null
											? ''
											: showLabel(
													details.data.consentObtained,
													CONSENT_OBTAINED_LIST
												)
									}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.remarks(EnquiryRecord)')}
									value={details?.data.enquiryRecordRemarks}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.remarks(MembershipApplication)')}
									value={details?.data.membershipApplicationRemarks}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.remarks(FaceSheet)')}
									value={details?.data.faceSheetRemarks}
								/>
							</Grid>
						</Grid>
					</AccordionDetails>
				</Accordion>
				{/* Results  */}
				<Accordion
					defaultExpanded
					sx={{
						borderRadius: '16px!important',
						'&::before': {
							backgroundColor: 'transparent !important',
						},
						marginBottom: '24px',
						padding: '8px',
					}}
				>
					<AccordionSummary expandIcon={<ExpandMoreIcon />}>
						<Typography
							component='span'
							sx={{ width: '99%', flexShrink: 1, fontWeight: 'bold' }}
						>
							{t('preAdmission.results')}
						</Typography>
						{canEditPreAdmission && (
							<Button
								onClick={(e) => {
									e.stopPropagation();
									navigate(
										`/pre-admission/pre-admission-list/detail/${preAdmissionId}/edit-results`
									);
								}}
								sx={{ marginRight: '24px', textTransform: 'none' }}
								variant='outlined'
							>
								{t('button.edit')}
							</Button>
						)}
					</AccordionSummary>
					<AccordionDetails>
						<Grid container spacing={3}>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.results')}
									valueElement={
										<>
											{details?.data.resultHasContacted && (
												<Typography variant='body2'>
													{t('preAdmission.resultsText1')}
												</Typography>
											)}
											{details?.data.resultCanContacted && (
												<Typography variant='body2'>
													{t('preAdmission.resultsText2Full', {
														// Records saved before the choice existed fall
														// back to the paper form's uncircled wording.
														target:
															details?.data.resultCanContactedTarget == null
																? RESULT_CAN_CONTACTED_TARGET_LIST.map(
																		(option) => option.name
																	).join(' / ')
																: showLabel(
																		details.data.resultCanContactedTarget,
																		RESULT_CAN_CONTACTED_TARGET_LIST
																	),
													})}
												</Typography>
											)}
											{details?.data.resultNeedDiscuss && (
												<Typography variant='body2'>
													{t('preAdmission.resultsText3')}
												</Typography>
											)}
											{details?.data.resultNotSuitable && (
												<Typography variant='body2'>
													{`${t('preAdmission.resultsText4')} ${
														details?.data.resultNotSuitableBecause
													}`}
												</Typography>
											)}
										</>
									}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.notSuitableReason')}
									value={details?.data.notSuitableReasonDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.notSuitableReasonSpecify')}
									value={details?.data.notSuitableReasonSpecify}
								/>
							</Grid>
						</Grid>
					</AccordionDetails>
				</Accordion>
			</Box>
		</>
	);
}
